import { ButtonV3, TextInput, TypographyV3 } from '@jouzen/ecom-components';
import { EmailValidator } from 'commons-validator-js';
import { useFormik } from 'formik';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';

import { EventType } from '@/analytics/types';
import { useWizardStateContext } from '@/app/[locale]/product/_contexts/WizardStateProvider';
import type { UseWizardState } from '@/app/[locale]/product/_hooks/useWizardState/types';

interface FormValues {
  email: string;
}

export interface EmailFormContentProps {
  readonly setEmailSubmitted: Dispatch<SetStateAction<boolean>>;
}

const EmailFormContent = ({
  setEmailSubmitted,
}: EmailFormContentProps): JSX.Element => {
  const { currentRoute, product, setNotifyModal }: UseWizardState =
    useWizardStateContext();
  const t = useTranslations();
  const path = usePathname();

  const { finish, size } = currentRoute;
  const [sizeToNotify, setSizeToNotify] = useState<string[]>([]);
  const sizeVal = size.value;

  const handleEmailNotifyMeAnalytics = async (
    values: FormValues,
  ): Promise<void> => {
    const baseSku = product?.variants[0]?.sku;
    const oosRingSku =
      size.value.length === 1
        ? baseSku + '-0' + size.value
        : baseSku + '-' + size.value;
    const email = values.email.toLowerCase();
    await window.ouraAnalytics.identify({ email });
    await window.ouraAnalytics.track(EventType.UserEmailCaptured);
    await window.ouraAnalytics.track(EventType.EmailOOSNotificationSignup, {
      oosRingUrl: path,
      oosRingSku,
      email,
    });
  };

  const capitalizeFirstLetter = (word: string) => {
    const finishStrings = word.split('-');
    const title = finishStrings.map((string) => {
      return string.charAt(0).toUpperCase() + string.slice(1);
    });
    return title.join(' ');
  };

  const finishCapitalized = capitalizeFirstLetter(finish.value);

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    onSubmit: async (values) => {
      setEmailSubmitted(true);
      setNotifyModal(false);
      setSizeToNotify([...sizeToNotify, sizeVal]);
      await handleEmailNotifyMeAnalytics(values);
      formik.resetForm();
    },
    validate: (values) => {
      const validator = new EmailValidator();
      const sanitized = values.email.trim().toLowerCase();

      if (!sanitized) return { email: t('checkout_form_emailrequired') };

      if (!validator.isValid(sanitized))
        return { email: t('checkout_form_emailformat') };

      return {};
    },
    validateOnChange: false,
    validateOnBlur: false,
  });

  useEffect(() => {
    if (!sizeToNotify.includes(sizeVal)) {
      setEmailSubmitted(false);
    }
  }, [sizeVal, sizeToNotify, setEmailSubmitted]);

  return (
    <div className="pt-2">
      <TypographyV3 color="inherit" className="text-xl">
        {t('inventory_notify_module_header', {
          style: '',
          finish: finishCapitalized,
          size: sizeVal,
        })}
      </TypographyV3>
      <TypographyV3 color="inherit" className="py-5 text-lg">
        {t('inventory_notify_module_subtext')}
      </TypographyV3>
      <form className="w-full" onSubmit={formik.handleSubmit} noValidate>
        <label htmlFor="email" className="pb-4">
          {t('inventory_notify_module_form_title')}
        </label>
        <TextInput
          className="mt-3"
          data-cy="notifyModalEmailInput"
          name="email"
          onChange={formik.handleChange}
          required
          type="email"
          value={formik.values.email}
        />
        {formik.errors.email && (
          <TypographyV3 className="mt-2 text-red">
            {formik.errors.email}
          </TypographyV3>
        )}
        <ButtonV3
          className="mt-6 min-w-full"
          data-cy="notifyModalEmailCTA"
          type="submit"
          variant="primary"
        >
          {t('inventory_notify_module_get_notified')}
        </ButtonV3>
      </form>
    </div>
  );
};

export default EmailFormContent;
